import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface StepperStep {
  id: string;
  title: string;
  status: 'completed' | 'active' | 'upcoming';
  statusText: string;
}

/**
 * Progress Stepper Service
 * Centralized service to manage multi-step progress state across the application
 * Provides methods to update current step and automatically calculates step statuses
 */
@Injectable({
  providedIn: 'root'
})
export class ProgressStepperService {
  
  /**
   * Predefined steps for the business quote process
   * This configuration is shared across all components
   */
  private readonly stepDefinitions: Omit<StepperStep, 'status' | 'statusText'>[] = [
    { id: 'supply-details', title: 'Supply details' },
    { id: 'generate-quotes', title: 'Choose tariff' },
    { id: 'your-details', title: 'Your details' },
    { id: 'review-submit', title: 'Review & submit' }
  ];

  /**
   * Current active step index (0-based)
   */
  private currentStepIndex = new BehaviorSubject<number>(1); // Default to contact-details (index 1)

  /**
   * Observable stream of the complete steps array with calculated statuses
   */
  public steps$: Observable<StepperStep[]>;

  constructor() {
    // Create reactive stream that recalculates steps whenever currentStepIndex changes
    this.steps$ = this.currentStepIndex.asObservable().pipe(
      map(activeIndex => this.calculateStepStatuses(activeIndex))
    );
  }

  /**
   * Sets the current active step by step ID
   * @param stepId - The ID of the step to make active
   */
  setCurrentStep(stepId: string): void {
    const index = this.stepDefinitions.findIndex(step => step.id === stepId);
    if (index !== -1) {
      this.currentStepIndex.next(index);
    }
  }

  /**
   * Sets the current active step by index
   * @param index - The 0-based index of the step to make active
   */
  setCurrentStepByIndex(index: number): void {
    if (index >= 0 && index < this.stepDefinitions.length) {
      this.currentStepIndex.next(index);
    }
  }

  /**
   * Gets the total number of steps in the process
   * @returns The total number of steps
   */
  getTotalSteps(): number {
    return this.stepDefinitions.length;
  }

  /**
   * Gets the current step index
   * @returns The current active step index
   */
  getCurrentStepIndex(): number {
    return this.currentStepIndex.value;
  }

  /**
   * Gets the current step ID
   * @returns The current active step ID
   */
  getCurrentStepId(): string {
    const index = this.currentStepIndex.value;
    return this.stepDefinitions[index]?.id || '';
  }

  /**
   * Moves to the next step in the sequence
   */
  nextStep(): void {
    const currentIndex = this.currentStepIndex.value;
    if (currentIndex < this.stepDefinitions.length - 1) {
      this.currentStepIndex.next(currentIndex + 1);
    }
  }

  /**
   * Moves to the previous step in the sequence
   */
  previousStep(): void {
    const currentIndex = this.currentStepIndex.value;
    if (currentIndex > 0) {
      this.currentStepIndex.next(currentIndex - 1);
    }
  }

  /**
   * Resets the stepper to the first step
   */
  reset(): void {
    this.currentStepIndex.next(0);
  }

  /**
   * Calculates the status and statusText for each step based on the active step index
   * @param activeIndex - The index of the currently active step
   * @returns Array of steps with calculated statuses
   */
  private calculateStepStatuses(activeIndex: number): StepperStep[] {
    return this.stepDefinitions.map((step, index) => ({
      ...step,
      status: this.getStepStatus(index, activeIndex),
      statusText: this.getStepStatusText(index, activeIndex)
    }));
  }

  /**
   * Determines the status of a step based on its position relative to the active step
   * @param stepIndex - The index of the step to evaluate
   * @param activeIndex - The index of the currently active step
   * @returns The status of the step
   */
  private getStepStatus(stepIndex: number, activeIndex: number): 'completed' | 'active' | 'upcoming' {
    if (stepIndex < activeIndex) return 'completed';
    if (stepIndex === activeIndex) return 'active';
    return 'upcoming';
  }

  /**
   * Determines the status text for a step based on its position relative to the active step
   * @param stepIndex - The index of the step to evaluate
   * @param activeIndex - The index of the currently active step
   * @returns The status text for the step
   */
  private getStepStatusText(stepIndex: number, activeIndex: number): string {
    if (stepIndex < activeIndex) return 'Completed';
    if (stepIndex === activeIndex) return 'Current step';
    return 'Upcoming';
  }
}
